import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faCheckCircle,
  faCircleHalfStroke,
  faClipboardCheck,
  faClock,
  faFireFlameCurved,
  faHourglassHalf,
  faLayerGroup,
  faRotate,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, ResponsiveContainer, LineChart, Line,
} from "recharts";
import { getMyTaskListPaged } from "../Tasks/taskService";

const BRAND = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const STATUS_META = {
  "pending":     { color: "#6366f1", label: "Pending" },
  "in progress": { color: "#3b82f6", label: "In Progress" },
  "waiting":     { color: "#f59e0b", label: "Waiting" },
  "forwarded":   { color: "#8b5cf6", label: "Forwarded" },
  "completed":   { color: "#10b981", label: "Completed" },
  "cancelled":   { color: "#64748b", label: "Cancelled" },
};
const colorForStatus  = (s) => STATUS_META[String(s ?? "").toLowerCase()]?.color ?? "#94a3b8";
const labelForStatus  = (s) => STATUS_META[String(s ?? "").toLowerCase()]?.label ?? s;

const PRIORITY_META = {
  low:    { color: "#10b981", label: "Low" },
  medium: { color: "#f59e0b", label: "Medium" },
  high:   { color: "#ef4444", label: "High" },
  urgent: { color: "#dc2626", label: "Urgent" },
};
const colorForPriority = (p) => PRIORITY_META[String(p ?? "").toLowerCase()]?.color ?? "#94a3b8";

const safeParseJson = (v) => { try { return JSON.parse(v); } catch { return null; } };

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "2-digit" }).format(new Date(v));
  } catch { return String(v); }
};

const formatRelative = (v) => {
  if (!v) return "—";
  try {
    const diff = Date.now() - new Date(v).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(v);
  } catch { return formatDate(v); }
};

const isDueToday = (v) => {
  if (!v) return false;
  const d = new Date(v);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const isOverdue = (v) => {
  if (!v) return false;
  return new Date(v).setHours(23, 59, 59, 999) < Date.now();
};

const monthLabel = (date) =>
  new Intl.DateTimeFormat(undefined, { month: "short" }).format(date);

// Build current-month date range
const getMonthRange = (offsetMonths = 0) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0);
  const toISO = (d) => d.toISOString().split("T")[0];
  return { start: toISO(start), end: toISO(end), label: monthLabel(start) };
};

// ── Reusable pieces ────────────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, sub, accent, highlight }) => (
  <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition dark:bg-[#161B22] ${highlight ? "border-rose-300 dark:border-rose-700" : "border-gray-100 dark:border-gray-700"}`}>
    <div className="flex items-start gap-4 p-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: accent ?? BRAND }}>
        <FontAwesomeIcon icon={icon} className="text-base" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-0.5 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{value}</div>
        {sub ? <div className={`mt-0.5 text-xs ${highlight ? "font-bold text-rose-500" : "text-slate-500 dark:text-slate-400"}`}>{sub}</div> : null}
      </div>
    </div>
    <div className="h-0.5 w-full" style={{ background: accent ?? BRAND }} />
  </div>
);

const ChartCard = ({ title, icon, children, aside, className = "" }) => (
  <div className={`overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22] ${className}`}>
    <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
          <FontAwesomeIcon icon={icon} />
        </span>
        <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">{title}</h3>
      </div>
      {aside ? <div className="text-xs text-slate-400 dark:text-slate-500">{aside}</div> : null}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      {label ? <div className="mb-1 font-bold text-white">{label}</div> : null}
      {payload.map((p) => (
        <div key={p.dataKey ?? p.name} className="flex items-center gap-2 text-gray-300">
          <span className="h-2 w-2 rounded-full" style={{ background: p.fill ?? p.color }} />
          {p.name}: <span className="font-semibold text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      <div className="flex items-center gap-2 font-bold text-white">
        <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />
        {name}
      </div>
      <div className="mt-0.5 text-gray-300">{value} tasks</div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const c = colorForStatus(status);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
      style={{ borderColor: `${c}55`, background: `${c}18`, color: c }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {labelForStatus(status)}
    </span>
  );
};

const PriorityDot = ({ priority }) => {
  const c = colorForPriority(priority);
  return <span className="inline-block h-2 w-2 rounded-full" style={{ background: c }} title={priority} />;
};

// ── Month picker tabs ──────────────────────────────────────────────────────────

const MONTH_OPTIONS = [
  { offset: 0, label: "This Month" },
  { offset: -1, label: "Last Month" },
  { offset: -2 },
  { offset: -3 },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function MyTasksDashboard({ displayName }) {
  const user    = useMemo(() => safeParseJson(localStorage.getItem("user")), []);
  const userId  = useMemo(() => user?.userId ?? user?.UserId ?? user?.id ?? user?.Id ?? "", [user]);
  const name    = useMemo(() => user?.fullName ?? user?.FullName ?? user?.userName ?? displayName ?? "You", [user, displayName]);

  // Month offset: 0 = current month, -1 = last month, etc.
  const [monthOffset, setMonthOffset] = useState(0);
  const monthRange = useMemo(() => getMonthRange(monthOffset), [monthOffset]);

  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      // Fetch up to 200 tasks — enough for monthly dashboard stats
      const result = await getMyTaskListPaged({
        userId,
        pageNumber: 1,
        pageSize: 200,
        sortDirection: "DESC",
      });
      setTasks(Array.isArray(result?.items) ? result.items : []);
    } catch (err) {
      setError(err?.message || "Tasks could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // ── Filter tasks to selected month ────────────────────────────────────────
  const monthTasks = useMemo(() => {
    const start = new Date(monthRange.start).getTime();
    const end   = new Date(monthRange.end).setHours(23, 59, 59, 999);
    return tasks.filter((t) => {
      const d = new Date(t.createdAt ?? t.dueDate ?? t.updatedAt ?? 0).getTime();
      return d >= start && d <= end;
    });
  }, [tasks, monthRange]);

  // ── Counts ────────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const statusMap = {};
    let overdue = 0, dueToday = 0, highPriority = 0;
    for (const t of monthTasks) {
      const s = String(t.status ?? t.taskStatus ?? "").toLowerCase();
      statusMap[s] = (statusMap[s] || 0) + 1;
      const due = t.dueDate ?? t.taskDueDate;
      const notDone = s !== "completed" && s !== "cancelled";
      if (notDone && isOverdue(due)) overdue++;
      if (notDone && isDueToday(due)) dueToday++;
      const p = String(t.priority ?? t.taskPriority ?? "").toLowerCase();
      if ((p === "high" || p === "urgent") && notDone) highPriority++;
    }
    const total     = monthTasks.length;
    const completed = statusMap["completed"] || 0;
    const active    = total - (statusMap["completed"] || 0) - (statusMap["cancelled"] || 0);
    return { total, completed, active, overdue, dueToday, highPriority, statusMap };
  }, [monthTasks]);

  // ── Charts ────────────────────────────────────────────────────────────────

  // Status pie
  const statusPieData = useMemo(() =>
    Object.entries(counts.statusMap)
      .map(([s, v]) => ({ name: labelForStatus(s), value: v, fill: colorForStatus(s) }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value),
  [counts.statusMap]);

  // Daily creation trend (current month only)
  const dailyTrend = useMemo(() => {
    if (monthOffset !== 0) return [];
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const buckets = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, total: 0, completed: 0 }));
    for (const t of monthTasks) {
      const d = new Date(t.createdAt ?? t.updatedAt ?? 0);
      if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
        const idx = d.getDate() - 1;
        if (buckets[idx]) {
          buckets[idx].total++;
          const s = String(t.status ?? t.taskStatus ?? "").toLowerCase();
          if (s === "completed") buckets[idx].completed++;
        }
      }
    }
    // Only show up to today
    return buckets.slice(0, now.getDate()).map((b) => ({ ...b, label: `${b.day}` }));
  }, [monthTasks, monthOffset]);

  // Priority bar
  const priorityData = useMemo(() => {
    const map = {};
    for (const t of monthTasks) {
      const p = String(t.priority ?? t.taskPriority ?? "unknown").toLowerCase();
      map[p] = (map[p] || 0) + 1;
    }
    return ["urgent", "high", "medium", "low"]
      .filter((p) => map[p])
      .map((p) => ({ name: PRIORITY_META[p]?.label ?? p, value: map[p], fill: colorForPriority(p) }));
  }, [monthTasks]);

  // Recent 6 active tasks
  const recentActive = useMemo(() =>
    monthTasks
      .filter((t) => {
        const s = String(t.status ?? t.taskStatus ?? "").toLowerCase();
        return s !== "completed" && s !== "cancelled";
      })
      .slice(0, 6),
  [monthTasks]);

  // Completion rate
  const completionRate = useMemo(() =>
    counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0,
  [counts]);

  // Month tab labels
  const monthTabs = useMemo(() =>
    MONTH_OPTIONS.map((opt) => {
      const r = getMonthRange(opt.offset);
      return { offset: opt.offset, label: opt.label ?? r.label };
    }),
  []);

  if (loading) return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: "#C04C2A" }} />
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Loading your tasks…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-800 dark:bg-rose-900/20">
      <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <span className="text-sm font-semibold">{error}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: BRAND }}>
        <div className="pointer-events-none absolute -right-14 -top-14 h-52 w-52 rounded-full bg-white/5 blur-3xl" />
        <div className="pointer-events-none absolute -left-8 bottom-0 h-32 w-32 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right,rgba(255,255,255,0.35),rgba(255,255,255,0.05) 60%,transparent)" }} />

        <div className="relative flex flex-col gap-4 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-100">
              <FontAwesomeIcon icon={faClipboardCheck} className="text-[9px]" />
              My Tasks
            </div>
            <h1 className="mt-2 text-xl font-extrabold tracking-tight drop-shadow-sm sm:text-2xl">
              {name}'s Task Overview
            </h1>
            <p className="mt-0.5 text-sm text-orange-100">
              {monthRange.label} · {counts.total} tasks
              {counts.overdue > 0 ? ` · ${counts.overdue} overdue` : ""}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/25"
            >
              <FontAwesomeIcon icon={faRotate} />
              Refresh
            </button>
            <Link
              to="/my-tasks"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-xs font-bold shadow-sm transition hover:bg-orange-50"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
              All My Tasks
            </Link>
          </div>
        </div>

        {/* Status strip */}
        <div className="relative grid grid-cols-3 gap-px border-t border-white/10 bg-white/10 sm:grid-cols-6">
          {[
            { label: "Total",     value: counts.total,       color: "#fff" },
            { label: "Active",    value: counts.active,      color: "#fbbf24" },
            { label: "Done",      value: counts.completed,   color: "#34d399" },
            { label: "Overdue",   value: counts.overdue,     color: counts.overdue > 0 ? "#f87171" : "#fff" },
            { label: "Due Today", value: counts.dueToday,    color: counts.dueToday > 0 ? "#fbbf24" : "#fff" },
            { label: "High/Urg",  value: counts.highPriority,color: counts.highPriority > 0 ? "#f87171" : "#fff" },
          ].map((item) => (
            <div key={item.label} className="bg-white/5 px-3 py-3 text-center">
              <div className="text-xl font-extrabold" style={{ color: item.color }}>{item.value}</div>
              <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-orange-100">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Month picker ── */}
      <div className="flex flex-wrap gap-2">
        {monthTabs.map((tab) => (
          <button
            key={tab.offset}
            onClick={() => setMonthOffset(tab.offset)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
              monthOffset === tab.offset
                ? "text-white shadow-sm"
                : "border border-gray-200 bg-white text-slate-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-300"
            }`}
            style={monthOffset === tab.offset ? { background: BRAND } : {}}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon={faLayerGroup}      label="Total Tasks"    value={counts.total}        sub={monthRange.label}         accent="linear-gradient(135deg,#C04C2A,#4A1B13)" />
        <KpiCard icon={faHourglassHalf}   label="Active"         value={counts.active}       sub="Not done yet"             accent="linear-gradient(135deg,#3b82f6,#1d4ed8)" />
        <KpiCard icon={faCheckCircle}     label="Completed"      value={counts.completed}    sub={`${completionRate}% rate`} accent="linear-gradient(135deg,#10b981,#059669)" />
        <KpiCard icon={faFireFlameCurved} label="Overdue"        value={counts.overdue}      sub={counts.overdue > 0 ? "Needs attention" : "All on track"} accent={counts.overdue > 0 ? "linear-gradient(135deg,#ef4444,#b91c1c)" : "linear-gradient(135deg,#64748b,#475569)"} highlight={counts.overdue > 0} />
      </div>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

        {/* Status pie */}
        <ChartCard title="Status Breakdown" icon={faCircleHalfStroke}>
          {statusPieData.length ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={46} outerRadius={70} paddingAngle={3}>
                    {statusPieData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-col gap-1.5">
                {statusPieData.map((s) => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.fill }} />
                      <span className="font-medium text-slate-700 dark:text-slate-300">{s.name}</span>
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-40 items-center justify-center text-xs text-slate-400">No tasks this period</div>
          )}
        </ChartCard>

        {/* Daily trend (this month) or priority bar (past months) */}
        {monthOffset === 0 && dailyTrend.length ? (
          <ChartCard title="Daily Activity (This Month)" icon={faArrowTrendUp} className="lg:col-span-2"
            aside={`Day 1–${dailyTrend.length}`}>
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={dailyTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#9ca3af" }} interval={Math.floor(dailyTrend.length / 8)} />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="total"     name="Total"     stroke="#C04C2A" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="completed" name="Completed" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        ) : (
          <ChartCard title="Tasks by Priority" icon={faFireFlameCurved} className="lg:col-span-2">
            {priorityData.length ? (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={priorityData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Tasks" radius={[6, 6, 0, 0]}>
                    {priorityData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[210px] items-center justify-center text-xs text-slate-400">No data</div>
            )}
          </ChartCard>
        )}
      </div>

      {/* ── Completion rate bar ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#161B22]">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-slate-800 dark:text-white">Completion Rate — {monthRange.label}</div>
          <div className="text-lg font-extrabold" style={{ color: "#C04C2A" }}>{completionRate}%</div>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${completionRate}%`, background: BRAND }}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400">
          <span>{counts.total} total</span>
          <span>{counts.completed} completed</span>
          <span>{counts.active} active</span>
          {counts.overdue > 0 ? <span className="font-semibold text-rose-500">{counts.overdue} overdue</span> : null}
        </div>
      </div>

      {/* ── Active tasks list ── */}
      <ChartCard title="Active Tasks This Period" icon={faClipboardCheck}
        aside={
          <Link to="/my-tasks" className="text-xs font-semibold hover:underline" style={{ color: "#C04C2A" }}>
            View all →
          </Link>
        }>
        {recentActive.length ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800 -mx-4 -mb-4">
            {recentActive.map((t) => {
              const id      = t.id ?? t.taskId ?? 0;
              const title   = t.title ?? t.taskTitle ?? "Untitled Task";
              const status  = t.status ?? t.taskStatus ?? "";
              const prio    = t.priority ?? t.taskPriority ?? "";
              const due     = t.dueDate ?? t.taskDueDate;
              const ticket  = t.ticketNumber ?? t.ticketSubject ?? "";
              const prog    = Number(t.progressPercent ?? t.taskProgressPercent ?? 0);
              const overdue = isOverdue(due);
              const today   = isDueToday(due);

              return (
                <div key={`${id}-${t.historyId ?? ""}`} className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition">
                  {/* Priority dot + progress ring */}
                  <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
                    <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15" fill="none" stroke={colorForStatus(status)} strokeWidth="3"
                        strokeDasharray={`${(prog / 100) * 94.25} 94.25`} strokeLinecap="round" />
                    </svg>
                    <PriorityDot priority={prio} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-800 dark:text-white">{title}</div>
                        {ticket ? <div className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">{ticket}</div> : null}
                      </div>
                      <StatusBadge status={status} />
                    </div>

                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {due ? (
                        <span className={`text-[10px] font-semibold ${overdue ? "text-rose-500" : today ? "text-amber-500" : "text-slate-400 dark:text-slate-500"}`}>
                          <FontAwesomeIcon icon={faClock} className="mr-1" />
                          {overdue ? "Overdue" : today ? "Due today" : `Due ${formatDate(due)}`}
                        </span>
                      ) : null}
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{prog}% done</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="text-sm font-semibold text-slate-600 dark:text-slate-400">
              {counts.total > 0 ? "All tasks completed this period!" : "No tasks this period"}
            </div>
          </div>
        )}
      </ChartCard>

    </div>
  );
}
